import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Plus, MapPin, IndianRupee, Pencil, Search, Filter } from "lucide-react";
import { toast } from "sonner";

const PROJECT_TYPES = ["Architecture", "Interior Design", "Construction", "Renovation", "Turnkey", "Consultancy", "Custom"];
const STATUSES = ["planning","in_progress","on_hold","completed"];

const statusColors = {
  planning: "bg-muted text-muted-foreground",
  in_progress: "bg-primary/15 text-primary",
  on_hold: "bg-destructive/15 text-destructive",
  completed: "bg-accent/20 text-accent",
};

const typeColors = {
  "Architecture": "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  "Interior Design": "bg-pink-500/15 text-pink-700 dark:text-pink-300",
  "Construction": "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  "Renovation": "bg-purple-500/15 text-purple-700 dark:text-purple-300",
  "Turnkey": "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  "Consultancy": "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
  "Custom": "bg-slate-500/15 text-slate-700 dark:text-slate-300",
};

const empty = { name: "", location: "", client_id: "", budget: 0, start_date: "", end_date: "", status: "planning", progress: 0, description: "", project_type: "Architecture", goals: "", auto_progress: true };

export default function Projects() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = () => api.get("/projects").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data));
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (e, p) => {
    e.preventDefault(); e.stopPropagation();
    setForm({
      name: p.name||"", location: p.location||"", client_id: p.client_id||"",
      budget: Number(p.budget||0), start_date: p.start_date||"", end_date: p.end_date||"",
      status: p.status||"planning", progress: Number(p.progress||0), description: p.description||"",
      project_type: p.project_type || "Architecture", goals: p.goals || "",
      auto_progress: p.auto_progress !== false,
    });
    setEditId(p.id); setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editId) { await api.patch(`/projects/${editId}`, form); toast.success("Project updated"); }
      else { await api.post("/projects", form); toast.success("Project created"); }
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = rows.filter(p => {
    if (filterType !== "all" && (p.project_type || "Architecture") !== filterType) return false;
    if (filterStatus !== "all" && p.status !== filterStatus) return false;
    if (q && !`${p.name} ${p.location||""} ${p.code||""} ${p.description||""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const typeCounts = PROJECT_TYPES.reduce((acc, t) => {
    acc[t] = rows.filter(p => (p.project_type||"Architecture") === t).length; return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Studio</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Projects</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} of {rows.length} projects</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-project-btn" onClick={openNew} className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" />New Project</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Edit Project" : "Create Project"}</DialogTitle><DialogDescription>Project type controls categorisation, filtering, and downstream templates.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" /></div>
              <div><Label>Project Type</Label>
                <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                  <SelectTrigger data-testid="project-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label>Budget (₹)</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })} /></div>
              <div><Label>Progress % (auto if enabled)</Label><Input type="number" min="0" max="100" value={form.progress} onChange={(e) => setForm({ ...form, progress: Number(e.target.value) })} disabled={form.auto_progress} /></div>
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="auto-progress" checked={form.auto_progress} onChange={(e) => setForm({ ...form, auto_progress: e.target.checked })} className="w-4 h-4 accent-primary" />
                <Label htmlFor="auto-progress" className="text-sm">Auto-calculate project progress from works, scope & phases</Label>
              </div>
              <div className="col-span-2"><Label>Goals / Objective</Label><Textarea rows={2} value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} placeholder="What are we trying to achieve?" /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <Button onClick={save} data-testid="save-project-btn" className="bg-primary text-primary-foreground">{editId ? "Update" : "Save"}</Button>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center rounded-2xl bg-card border border-border/60 p-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" className="pl-9" data-testid="projects-search" />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48 h-9" data-testid="filter-project-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types ({rows.length})</SelectItem>
              {PROJECT_TYPES.map(t => <SelectItem key={t} value={t}>{t} ({typeCounts[t]||0})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 h-9" data-testid="filter-project-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_"," ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((p) => (
          <Link to={`/projects/${p.id}`} key={p.id} data-testid={`project-${p.id}`}
            className="rounded-2xl p-6 bg-card border border-border/60 hover:border-primary/50 hover:-translate-y-0.5 transition-all block relative">
            <button onClick={(e) => openEdit(e, p)} data-testid={`edit-project-${p.id}`} className="absolute top-4 right-4 p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-primary z-10" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
            <div className="flex items-start justify-between pr-8">
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{p.code}</div>
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${statusColors[p.status] || "bg-muted"}`}>{p.status?.replace("_"," ")}</span>
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full ${typeColors[p.project_type||"Architecture"]}`} data-testid={`project-type-${p.id}`}>{p.project_type || "Architecture"}</span>
              {p.auto_progress !== false && <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">auto</span>}
            </div>
            <div className="mt-3 font-display text-xl font-semibold">{p.name}</div>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              {p.location && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{p.location}</div>}
              <div className="flex items-center gap-2"><IndianRupee className="w-3.5 h-3.5" />{Number(p.budget || 0).toLocaleString("en-IN")}</div>
            </div>
            <div className="mt-5">
              <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5">
                <span>Progress</span><span>{p.progress}%</span>
              </div>
              <Progress value={p.progress} className="h-1.5" />
            </div>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full p-10 rounded-2xl border border-dashed border-border/60 text-center text-sm text-muted-foreground">
            No projects match your filters.
          </div>
        )}
      </div>
    </div>
  );
}
